import type React from 'react';
import { useCallback, useRef, useState } from 'react';

/**
 * useFeedbackOrchestration — feedback / celebration presentation state.
 *
 * Extracted from PracticeSession.tsx as part of the F-FE-1 god-component
 * split (K12 decision: custom hooks, not mini-components). Owns everything
 * that describes "how the answer landed" for the learner: motivational
 * copy, the running combo/streak, XP counters, streak-milestone and
 * daily-goal celebration flags, and the cheat-sheet nudge that fires after
 * repeated errors.
 *
 * This hook is deliberately pure state + setters + a reset helper. The
 * answer pipeline in useQuestionFlow drives these setters; the render tree
 * (FeedbackDisplay / FluencyCorrectState / FluencyIncorrectState /
 * ComboIndicator / PracticeHeaderMinimal) reads the values.
 *
 * HOOKS-ORDER SAFETY: this hook must be called unconditionally at the top
 * of PracticeSession, above every early return (see F-FE-1 gotcha #1).
 */
export interface StreakMilestone {
  count: number;
  message: string;
}
export interface FeedbackOrchestration {
  // Presentation state
  currentStreak: number;
  speedStreak: number;
  motivationalText: string;
  xpGained: number;
  totalXpGained: number;
  streakMilestone: StreakMilestone | null;
  dailyGoalReached: boolean;
  cheatSheetNudge: boolean;
  // Setters (consumed by the answer pipeline)
  setCurrentStreak: React.Dispatch<React.SetStateAction<number>>;
  setSpeedStreak: React.Dispatch<React.SetStateAction<number>>;
  setMotivationalText: React.Dispatch<React.SetStateAction<string>>;
  setXpGained: React.Dispatch<React.SetStateAction<number>>;
  setTotalXpGained: React.Dispatch<React.SetStateAction<number>>;
  setStreakMilestone: React.Dispatch<React.SetStateAction<StreakMilestone | null>>;
  setDailyGoalReached: React.Dispatch<React.SetStateAction<boolean>>;
  setCheatSheetNudge: React.Dispatch<React.SetStateAction<boolean>>;
  // Refs (session-scoped bookkeeping used by the frustration nudge)
  nudgeFiredRef: React.MutableRefObject<boolean>;
  consecutiveErrorsRef: React.MutableRefObject<number>;
  /** Reset all feedback state — called from the session reset. */
  resetFeedback: () => void;
}
export function useFeedbackOrchestration(): FeedbackOrchestration {
  const [currentStreak, setCurrentStreak] = useState(0);
  const [speedStreak, setSpeedStreak] = useState(0);
  const [motivationalText, setMotivationalText] = useState('');
  const [xpGained, setXpGained] = useState(0);
  const [totalXpGained, setTotalXpGained] = useState(0);
  const [streakMilestone, setStreakMilestone] = useState<StreakMilestone | null>(null);
  const [dailyGoalReached, setDailyGoalReached] = useState(false);
  const [cheatSheetNudge, setCheatSheetNudge] = useState(false);
  const nudgeFiredRef = useRef(false);
  const consecutiveErrorsRef = useRef(0);
  const resetFeedback = useCallback(() => {
    setCurrentStreak(0);
    setSpeedStreak(0);
    setMotivationalText('');
    setXpGained(0);
    setTotalXpGained(0);
    setStreakMilestone(null);
    setDailyGoalReached(false);
    setCheatSheetNudge(false);
    nudgeFiredRef.current = false;
    consecutiveErrorsRef.current = 0;
  }, []);
  return {
    currentStreak,
    speedStreak,
    motivationalText,
    xpGained,
    totalXpGained,
    streakMilestone,
    dailyGoalReached,
    cheatSheetNudge,
    setCurrentStreak,
    setSpeedStreak,
    setMotivationalText,
    setXpGained,
    setTotalXpGained,
    setStreakMilestone,
    setDailyGoalReached,
    setCheatSheetNudge,
    nudgeFiredRef,
    consecutiveErrorsRef,
    resetFeedback
  };
}